import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Icon } from "@/components/ui/Icon";
import { StatusPill } from "@/components/ui/StatusPill";
import { TicketIdChip } from "@/components/board/TicketChips";
import { LinkTicketDialog } from "@/components/board/LinkTicketDialog";
import { CreateFollowUpDialog } from "@/components/board/CreateFollowUpDialog";
import { useRowLinks } from "@/hooks/useRowLinks";
import { isTicketBoard, type ViewBoard, type ViewRow } from "@/lib/boardView";
import { linkLabel } from "@/lib/ticketLinks";
import { t } from "@/lib/strings";
import type { LinkedTicket, UUID } from "@/lib/types";

/**
 * "Verknüpfte Tickets" — peer links + Folgeticket subordination for a ticket.
 * Reads links in both directions and renders three groups (Übergeordnet /
 * Folgetickets / Verknüpft). Ticket boards only.
 */
export function TicketLinksPanel({ row, board }: { row: ViewRow; board: ViewBoard }) {
  const rowId = (row.raw?.id ?? row.id) as UUID;
  const boardId = board.id as UUID;
  const navigate = useNavigate();
  const { links, createLink, createFollowUp, removeLink } = useRowLinks(rowId, boardId);
  const [linkOpen, setLinkOpen] = useState(false);
  const [followOpen, setFollowOpen] = useState(false);

  if (!isTicketBoard(board)) return null;

  function open(item: LinkedTicket) {
    void navigate({
      to: "/b/$boardId",
      params: { boardId: item.board_id as UUID },
      search: { row: item.row_id } as Record<string, string>,
    });
  }

  const empty = !links.parent && links.follow_ups.length === 0 && links.related.length === 0;

  return (
    <div style={{ marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "center", marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink-2)", flex: 1 }}>
          {t.linksSectionTitle}
        </div>
        <button type="button" onClick={() => setLinkOpen(true)} style={addBtnStyle}>
          <Icon name="plus" size={13} color="var(--brand)" />
          {t.linkAction}
        </button>
        <button type="button" onClick={() => setFollowOpen(true)} style={addBtnStyle}>
          <Icon name="plus" size={13} color="var(--brand)" />
          {t.followUpAction}
        </button>
      </div>

      {empty ? (
        <div style={{ fontSize: 12.5, color: "var(--ink-3)", padding: "2px 0 4px" }}>
          {t.linksEmpty}
        </div>
      ) : (
        <>
          {links.parent ? (
            <LinkGroup label={t.linksParentGroup}>
              <LinkRow item={links.parent} onOpen={open} onRemove={removeLink.mutate} />
            </LinkGroup>
          ) : null}
          {links.follow_ups.length > 0 ? (
            <LinkGroup label={t.linksFollowUpsGroup}>
              {links.follow_ups.map((it) => (
                <LinkRow key={it.link_id} item={it} onOpen={open} onRemove={removeLink.mutate} />
              ))}
            </LinkGroup>
          ) : null}
          {links.related.length > 0 ? (
            <LinkGroup label={t.linksRelatedGroup}>
              {links.related.map((it) => (
                <LinkRow
                  key={it.link_id}
                  item={it}
                  relation={linkLabel(it.link_type, it.direction)}
                  onOpen={open}
                  onRemove={removeLink.mutate}
                />
              ))}
            </LinkGroup>
          ) : null}
        </>
      )}

      <LinkTicketDialog
        open={linkOpen}
        onOpenChange={setLinkOpen}
        rowId={rowId}
        busy={createLink.isPending}
        onSubmit={(targetRowId, linkType) =>
          createLink.mutate({ targetRowId, linkType }, { onSuccess: () => setLinkOpen(false) })
        }
      />
      <CreateFollowUpDialog
        open={followOpen}
        onOpenChange={setFollowOpen}
        mainRowId={rowId}
        boardId={boardId}
        onCreated={() => createFollowUp.reset()}
      />
    </div>
  );
}

const addBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 4,
  marginLeft: 8,
  padding: "3px 8px",
  fontSize: 12,
  fontWeight: 500,
  color: "var(--brand)",
  background: "transparent",
  border: "1px solid var(--hairline)",
  borderRadius: 6,
  cursor: "pointer",
};

function LinkGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.3,
          color: "var(--ink-4)",
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>{children}</div>
    </div>
  );
}

function LinkRow({
  item,
  relation,
  onOpen,
  onRemove,
}: {
  item: LinkedTicket;
  relation?: string;
  onOpen: (item: LinkedTicket) => void;
  onRemove: (linkId: UUID) => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "6px 8px",
        borderRadius: 6,
        border: "1px solid var(--hairline-soft)",
        background: "var(--surface)",
      }}
    >
      {relation ? (
        <span style={{ fontSize: 11, color: "var(--ink-4)", flexShrink: 0 }}>{relation}</span>
      ) : null}
      <button
        type="button"
        onClick={() => onOpen(item)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flex: 1,
          minWidth: 0,
          background: "transparent",
          border: "none",
          textAlign: "left",
          cursor: "pointer",
          color: "var(--ink-1)",
        }}
      >
        <TicketIdChip label={item.ticket_label} />
        <span
          style={{
            fontSize: 13,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {item.title}
        </span>
        {item.status_label ? (
          <StatusPill option={{ label: item.status_label, color: item.status_color ?? "" }} />
        ) : null}
      </button>
      <button
        type="button"
        onClick={() => onRemove(item.link_id)}
        title={t.linkRemove}
        style={{
          flexShrink: 0,
          background: "transparent",
          border: "none",
          cursor: "pointer",
          color: "var(--ink-4)",
          padding: 2,
          display: "inline-flex",
        }}
      >
        <Icon name="x" size={14} color="var(--ink-4)" />
      </button>
    </div>
  );
}
